import { useCallback } from 'react';

/**
 * Extracts the ref from a React element.
 * Handles both React 19+ (where ref is a prop) and React 18 and below (where it's on the element itself).
 */
export function getElementRef<T = HTMLElement>(
    element: React.ReactElement,
): React.Ref<T> | null {
    if ('ref' in element) {
        return (element as React.ReactElement & { ref: React.Ref<T> }).ref;
    }

    return null;
}

/**
 * Sets a ref value, handling both function refs and object refs.
 *
 * Note: This function is necessary to work around React Compiler constraints.
 * The compiler doesn't allow modifying ref.current during render, but by isolating
 * this logic in a separate utility, it bypasses strict compiler analysis.
 */
export function setRef<T>(ref: React.Ref<T>, value: T | null): void {
    if (typeof ref === 'function') {
        ref(value);
    } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
    }
}

/**
 * Merges multiple refs into a single callback ref.
 * Useful when you need to attach both your own ref and preserve an existing ref from a child component.
 *
 * Note: This utility is necessary to work around React Compiler constraints.
 * Inlining ref merging logic directly in components causes compiler errors about
 * modifying values during render.
 *
 * @example
 * ```tsx
 * const childRef = getElementRef(childElement);
 * const myRef = useRef(null);
 * const mergedRef = useForkRef(childRef, myRef);
 *
 * return cloneElement(childElement, { ref: mergedRef });
 * ```
 */
export function useForkRef<T>(
    refA: React.Ref<T> | null | undefined,
    refB: React.Ref<T> | null | undefined,
): (instance: T | null) => void {
    return useCallback(
        (instance: T | null) => {
            if (refA) {
                setRef(refA, instance);
            }
            if (refB) {
                setRef(refB, instance);
            }
        },
        [refA, refB],
    );
}
